import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { TabItem, Tabs } from "@ui/tabs/Tabs";
import { Signal } from "badmfck-signal";
import { Cable, Check, Link2, Plug, Plus, RotateCw, Settings2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { GD } from "src/GD";
import { useAPI } from "src/hooks/useApi";
import type { ITerminal } from "src/types/types";
import { notify } from "../../tools/notify";
import { S_CHANNEL_ITEM_POPUP, type TPaymentChannel } from "../channelList/channelData";
import { FormParser } from "../FormParser";
import { S_CONFIRM } from "../popup/general/ConfirmDialog";
import { channelStatus, compareText, formatDate, matchesQuery, methodTypeLabel, plural, uniqueSorted } from "../providers/providersData";
import {
	Card,
	CardBrandIcons,
	Cell,
	Chip,
	DialogBody,
	DialogTabs,
	DirectoryCard,
	DirectoryRow,
	embeddedFormSx,
	EmptyState,
	EntityCell,
	FilterChips,
	FilterSelect,
	IconTile,
	Item,
	ItemList,
	LinkButton,
	MethodTile,
	Mono,
	Muted,
	Notice,
	Pill,
	ProviderDialog,
	RowChevron,
	SearchField,
	Section,
	SkeletonRows,
	StateDot,
	StatusPill,
	TabIntro,
	TabSurface,
	WarningText,
} from "../providers/providersUi";
import { AdminButton } from "./administration/AdminControls";

export type TIntegration = {
	created_at: any;
	created_by: number;
	data: any;
	id: number;
	name: string;
	project_id: number;
	status: string;
	type: string;
	updated_at?: any;
	updated_by?: number;
};

export const S_INTEGRATION_EDIT_POPUP = new Signal<{ integration_id: number; onSuccess?: () => void } | null>("S_INTEGRATION_EDIT_POPUP");

export const integrationHost = (integration?: TIntegration | null): string => integration?.data?.setup?.network?.host ?? "";
/** The payment method type the integration serves, e.g. "payment_card". */
export const integrationMethodType = (integration?: TIntegration | null): string => integration?.data?.setup?.common?.method ?? "";

const asList = (data: unknown): TIntegration[] => (Array.isArray(data) ? data : data && typeof data === "object" ? [data as TIntegration] : []);

type TStatusFilter = "all" | "active" | "disabled" | "deleted";

/* ---------- Integrations tab ---------- */

export const IntegrationsList = ({
	integrations,
	loading,
	busy,
	error,
	channels,
	methods,
	onReload,
	onShowChannels,
}: {
	integrations: TIntegration[];
	loading: boolean;
	busy: boolean;
	error?: IError;
	channels: TPaymentChannel[];
	methods: ITerminal[];
	onReload: () => void;
	onShowChannels: (integrationId: number) => void;
}) => {
	 useLocale();
	const [query, setQuery] = useState("");
	const [status, setStatus] = useState<TStatusFilter>("all");
	const [type, setType] = useState("");
	const usage = useMemo(() => {
		const counts = new Map<number, number>();
		for (const channel of channels) if (channel.integration?.id && channelStatus(channel) !== "deleted") counts.set(channel.integration.id, (counts.get(channel.integration.id) ?? 0) + 1);
		return counts;
	}, [channels]);
	const methodName = (value: string) => methods.find(method => method.type === value)?.name ?? methodTypeLabel(value);
	const types = uniqueSorted(integrations.map(item => item.type));
	const byStatus = (value: TStatusFilter) => integrations.filter(item => value === "all" || item.status === value).length;
	const visible = integrations
		.filter(item => status === "all" || item.status === status)
		.filter(item => !type || item.type === type)
		.filter(item => matchesQuery(query, [item.name, item.type, item.id, integrationHost(item), methodName(integrationMethodType(item))]))
		.sort((a, b) => compareText(a.name, b.name));
	const filtered = query || status !== "all" || type;
	const add = () => S_INTEGRATION_EDIT_POPUP.invoke({ integration_id: -1, onSuccess: onReload });

	return (
		<TabSurface>
			<TabIntro
				text={msg("anIntegrationIsTheTechnicalAccountAtAProviderHostAndApiKeysT_90a5c4ef")}
				actions={
					<>
						<AdminButton variant="secondarySmall" size="small" disabled={busy} onClick={onReload}>
							<RotateCw size={14} />{msg("refresh")}</AdminButton>
						<AdminButton variant="primarySmall" size="small" onClick={add}>
							<Plus size={14} />{msg("addIntegration")}</AdminButton>
					</>
				}
			/>
			{error && <Notice tone="error" action={<AdminButton variant="secondarySmall" size="small" onClick={onReload}>{msg("retry")}</AdminButton>}>{error.message}</Notice>}
			<DirectoryCard
				columns="minmax(240px, 1fr) minmax(70px, max-content) minmax(110px, max-content) minmax(120px, max-content) minmax(90px, max-content) minmax(110px, max-content)"
				head={["Integration", "Type", "Method", "Host", "Channels", "Status"]}
				toolbar={
					<>
						<SearchField value={query} onChange={setQuery} placeholder={msg("searchByNameHostOrId")} />
						<FilterChips
							label={msg("status")}
							value={status}
							onChange={setStatus}
							options={[
								{ id: "all", label: msg("all"), count: integrations.length },
								{ id: "active", label: msg("active"), count: byStatus("active") },
								{ id: "disabled", label: msg("disabled"), count: byStatus("disabled"), hidden: !byStatus("disabled") },
								{ id: "deleted", label: msg("deleted"), count: byStatus("deleted"), hidden: !byStatus("deleted") },
							]}
						/>
						{types.length > 1 && <FilterSelect label={msg("allTypes")} value={type} onChange={setType} options={types.map(value => ({ value, label: value }))} />}
					</>
				}
				footer={
					!loading &&
					integrations.length > 0 && (
						<>
							<span>{filtered ? msg("templateOf_64f93023", { v0: visible.length, v1: plural(integrations.length, "integration") }) : plural(integrations.length, "integration")}</span>
							{filtered && (
								<LinkButton
									onClick={() => {
										setQuery("");
										setStatus("all");
										setType("");
									}}>{msg("clearFilters")}</LinkButton>
							)}
						</>
					)
				}>
				{loading ? (
					<SkeletonRows />
				) : !integrations.length ? (
					<EmptyState
						icon={<Plug size={20} />}
						title={msg("noIntegrationsYet")}
						text={msg("addTheProviderAccountDetailsFirstThenConnectChannelsToIt")}
						action={
							<AdminButton variant="primarySmall" size="small" onClick={add}>
								<Plus size={14} />{msg("addIntegration")}</AdminButton>
						}
					/>
				) : !visible.length ? (
					<EmptyState icon={<Plug size={20} />} title={msg("nothingMatchesTheFilters")} text={msg("tryAnotherSearchOrClearTheFilters")} />
				) : (
					visible.map(integration => {
						const count = usage.get(integration.id) ?? 0;
						const method = integrationMethodType(integration);
						const host = integrationHost(integration);
						const updated = formatDate(integration.updated_at) ?? formatDate(integration.created_at);
						return (
							<DirectoryRow
								key={integration.id}
								label={msg("templateOpen_afaef5c3", { v0: integration.name })}
								muted={integration.status === "deleted"}
								onOpen={() => S_INTEGRATION_EDIT_POPUP.invoke({ integration_id: integration.id, onSuccess: onReload })}>
								<Cell kind="main">
									<EntityCell
										icon={
											<IconTile>
												<Plug />
											</IconTile>
										}
										name={integration.name || msg("templateIntegration_862916db", { v0: integration.id })}
										sub={
											<>
												<Mono>#{integration.id}</Mono>
												{updated && <span>{msg("updated_a919c7c2")}{updated}</span>}
											</>
										}
									/>
								</Cell>
								<Cell label={msg("type")}>{integration.type ? <Chip>{integration.type}</Chip> : <Muted>—</Muted>}</Cell>
								<Cell label={msg("method")}>
									{method ? (
										<span style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0 }}>
											<MethodTile type={method} small />
											{methodName(method)}
										</span>
									) : (
										<Muted>{msg("any")}</Muted>
									)}
								</Cell>
								<Cell label={msg("host")}>{host ? <Mono title={host}>{host}</Mono> : <Muted>—</Muted>}</Cell>
								<Cell label={msg("channels")}>
									{count ? (
										<LinkButton onClick={() => onShowChannels(integration.id)} title={msg("showTheseChannels")}>
											{plural(count, "channel")} →
										</LinkButton>
									) : (
										<Muted>{msg("notUsed")}</Muted>
									)}
								</Cell>
								<Cell kind="trail">
									<StatusPill status={integration.status} />
									<RowChevron />
								</Cell>
							</DirectoryRow>
						);
					})
				)}
			</DirectoryCard>
		</TabSurface>
	);
};

/* ---------- Integration dialog: settings and the channels that use it ---------- */

export const IntegrationEditorPopup = () => {
	useLocale();
	const request = S_INTEGRATION_EDIT_POPUP.use();
	if (!request) return null;
	return <IntegrationDialog key={request.integration_id} integrationId={request.integration_id} onSuccess={request.onSuccess} />;
};

const IntegrationDialog = ({ integrationId, onSuccess }: { integrationId: number; onSuccess?: () => void }) => {
	useLocale();
	const existing = integrationId > 0;
	const [summary, setSummary] = useState<TIntegration | null>(null);
	const [channels] = useAPI<TPaymentChannel[]>(existing ? { method: "payments/channels/list", data: {} } : null);
	// Deleted channels stay listed for reference, after the live ones, but are not counted.
	const used = (Array.isArray(channels?.data) ? channels.data : [])
		.filter(channel => channel.integration?.id === integrationId)
		.sort((a, b) => Number(channelStatus(a) === "deleted") - Number(channelStatus(b) === "deleted") || compareText(a.name, b.name));
	const live = used.filter(channel => channelStatus(channel) !== "deleted").length;
	const close = () => S_INTEGRATION_EDIT_POPUP.invoke(null);

	const form = (
		<FormParser
			appearance="modern"
			sx={embeddedFormSx}
			size="small"
			secondaryButton={null}
			onSubmit={async (data: any): Promise<any> => {
				const result = await GD.REQ_API.request({ method: "integrations/set", data });
				if (result.error) return result;
				notify.success(existing ? msg("integrationSaved") : msg("integrationCreated"));
				onSuccess?.();
				close();
				return result;
			}}
			onRequest={async (): Promise<any> => {
				const result = await GD.REQ_API.request({ method: "integrations/get", data: { id: integrationId } });
				const loaded = asList(result.data)[0];
				if (!result.error && loaded && existing) setSummary(loaded);
				return result;
			}}
		/>
	);

	return (
		<ProviderDialog
			width={existing ? 860 : 680}
			fullHeight={existing}
			eyebrow={msg("integration")}
			title={existing ? summary?.name || msg("templateIntegration_862916db", { v0: integrationId }) : msg("newIntegration")}
			meta={
				existing &&
				summary && (
					<>
						<StateDot status={summary.status} />
						{summary.type && <Chip>{summary.type}</Chip>}
						{integrationHost(summary) && <Mono>{integrationHost(summary)}</Mono>}
						<span className="push">#{summary.id}</span>
					</>
				)
			}
			onClose={close}>
			{existing ? (
				<DialogTabs>
					<Tabs variant="modern">
						<TabItem title={msg("settings")} icon={<Settings2 size={16} />}>
							<DialogBody>{form}</DialogBody>
						</TabItem>
						<TabItem title={channels ? msg("templateChannels_0a345dfb", { v0: live }) : msg("channels")} icon={<Cable size={16} />}>
							<DialogBody>
								<Section title={msg("channelsUsingThisIntegration")} count={channels ? live : undefined} />
								<Card>
									{!channels ? (
										<SkeletonRows rows={2} />
									) : !used.length ? (
										<EmptyState icon={<Cable size={20} />} title={msg("noChannelsUseItYet")} text={msg("openAChannelAndConnectItToThisIntegrationOnItsOverviewTab")} />
									) : (
										<ItemList>
											{used.map(channel => (
													<Item key={channel.id} label={msg("templateOpen_afaef5c3", { v0: channel.name })} onOpen={() => S_CHANNEL_ITEM_POPUP.invoke(channel)}>
														<MethodTile type={channel.terminal?.type} />
														<div className="item-main">
															<div style={{ color: "#182230", fontWeight: 600 }}>{channel.name}</div>
															<div style={{ color: "#667085", fontSize: 12 }}>
																{channel.provider?.name ?? "—"} · {channel.terminal?.name ?? methodTypeLabel(channel.terminal?.type)}
															</div>
														</div>
														<span className="item-side">
															{channel.currency && <Chip>{channel.currency}</Chip>}
															<CardBrandIcons instrument={channel.instrument_type} />
															<StatusPill status={channelStatus(channel)} />
														</span>
														<RowChevron />
													</Item>
												))}
										</ItemList>
									)}
								</Card>
							</DialogBody>
						</TabItem>
					</Tabs>
				</DialogTabs>
			) : (
				<DialogBody>{form}</DialogBody>
			)}
		</ProviderDialog>
	);
};

/* ---------- Choosing the integration of a channel ---------- */

export const S_INTEGRATION_SELECT_POPUP = new Signal<{
	channel_id: number;
	current_id?: number;
	channel_name?: string;
	/** The channel's payment method type; integrations serving it are listed first. */
	method_type?: string | null;
	onClose?: () => void;
	onSuccess?: () => void | Promise<void>;
} | null>("S_INTEGRATION_SELECT_POPUP");
export const S_INTEGRATION_SELECT_POPUP_CLOSED = new Signal<void>("S_INTEGRATION_SELECT_POPUP_CLOSED");

export const SelectIntegrationPopup = () => {
	useLocale();
	const request = S_INTEGRATION_SELECT_POPUP.use();
	if (!request) return null;
	return <SelectIntegrationDialog key={request.channel_id} request={request} />;
};

const SelectIntegrationDialog = ({ request }: { request: NonNullable<ReturnType<typeof S_INTEGRATION_SELECT_POPUP.use>> }) => {
	useLocale();
	const [error, setError] = useState<IError | null>(null);
	const [busy, setBusy] = useState(false);
	const [query, setQuery] = useState("");
	const [response] = useAPI<TIntegration[]>({ method: "integrations/get", data: {} });
	const [currentId, setCurrentId] = useState<number | undefined>(request.current_id);
	const fits = (item: TIntegration) => !request.method_type || !integrationMethodType(item) || integrationMethodType(item) === request.method_type;
	const integrations = asList(response?.data)
		.filter(item => item.status !== "deleted")
		.sort((a, b) => Number(fits(b)) - Number(fits(a)) || compareText(a.name, b.name));
	const visible = integrations.filter(item => matchesQuery(query, [item.name, item.type, item.id, integrationHost(item)]));

	// Older callers do not pass the current integration, so it is looked up once from the channel.
	useEffect(() => {
		if (request.current_id !== undefined) return;
		let active = true;
		void GD.REQ_API.request({ method: "integrations/get", data: { channel_id: request.channel_id } }).then(result => {
			const current = asList(result.data)[0];
			if (active && !result.error && current) setCurrentId(current.id);
		});
		return () => {
			active = false;
		};
	}, [request.channel_id, request.current_id]);

	const close = () => {
		setError(null);
		S_INTEGRATION_SELECT_POPUP.invoke(null);
		request.onClose?.();
		S_INTEGRATION_SELECT_POPUP_CLOSED.invoke();
	};

	const connect = async (integration: TIntegration) => {
		setError(null);
		setBusy(true);
		const result = await GD.REQ_API.request({ method: "integrations/connect", data: { channel_id: request.channel_id, id: integration.id } });
		setBusy(false);
		if (result.error) {
			setError(result.error);
			return;
		}
		notify.success(msg("integrationConnected"));
		await request.onSuccess?.();
		close();
	};

	const choose = (integration: TIntegration) =>
		S_CONFIRM.invoke({
			title: currentId ? msg("changeIntegration") : msg("connectIntegration"),
			message: currentId
				? msg("templateSwitchThisChannelToPaymentsThatAreAlreadyBeingProcessedM_fae110ad", { v0: integration.name })
				: msg("templateConnectThisChannelToItsPaymentsWillBeProcessedThroughThi_587d9a1c", { v0: integration.name }),
			confirm: currentId ? "Switch" : "Connect",
			onResult: value => {
				if (value === "confirm") void connect(integration);
			},
		});

	return (
		<ProviderDialog
			width={760}
			eyebrow={request.channel_name ? msg("templateChannel_58a1744e", { v0: request.channel_name }) : msg("templateChannel_e25c3617", { v0: request.channel_id })}
			title={currentId ? msg("changeIntegration") : msg("connectAnIntegration")}
			busy={busy}
			onClose={close}>
			<DialogBody>
				{(error || response?.error) && <Notice tone="error">{(error ?? response?.error)?.message}</Notice>}
				<div style={{ display: "flex", marginBottom: 12 }}>
					<SearchField value={query} onChange={setQuery} placeholder={msg("searchByNameTypeOrHost")} />
				</div>
				<Card>
					{!response ? (
						<SkeletonRows rows={3} />
					) : !visible.length ? (
						<EmptyState
							icon={<Plug size={20} />}
							title={integrations.length ? msg("nothingMatchesTheSearch") : msg("noIntegrationsYet")}
							text={integrations.length ? msg("tryAShorterQuery") : msg("createOneInProvidersIntegrationsFirst")}
						/>
					) : (
						<ItemList>
							{visible.map(integration => {
								const current = integration.id === currentId;
								return (
									<Item key={integration.id}>
										<IconTile>
											<Plug />
										</IconTile>
										<div className="item-main">
											<div style={{ color: "#182230", fontWeight: 600 }}>{integration.name}</div>
											<div style={{ display: "flex", flexWrap: "wrap", gap: "2px 8px", color: "#667085", fontSize: 12 }}>
												<span>{integration.type}</span>
												{integrationMethodType(integration) && <span>· {methodTypeLabel(integrationMethodType(integration))}</span>}
												{!fits(integration) && <WarningText>{msg("differentPaymentMethod")}</WarningText>}
												{integrationHost(integration) && (
													<span>
														· <Mono>{integrationHost(integration)}</Mono>
													</span>
												)}
											</div>
										</div>
										<span className="item-side">
											<StatusPill status={integration.status} />
											{current ? (
												<Pill tone="positive">
													<Check size={13} />{msg("connected")}</Pill>
											) : (
												<AdminButton variant={currentId || !fits(integration) ? "secondarySmall" : "primarySmall"} size="small" disabled={busy} onClick={() => choose(integration)}>
													<Link2 size={14} />
													{currentId ? msg("switch") : msg("connect")}
												</AdminButton>
											)}
										</span>
									</Item>
								);
							})}
						</ItemList>
					)}
				</Card>
			</DialogBody>
		</ProviderDialog>
	);
};
